<p-dialog
  [visible]="visible"
  (visibleChange)="visibleChange.emit($event)"
  [modal]="true"
  [header]="currentWork()?.assignmentAllowed ? 'Plan & Assign Field Resources' : 'Gather Field Work Requirements'"
  [draggable]="false"
  [resizable]="false"
  [closable]="!busy()"
  [closeOnEscape]="!busy()"
  appendTo="body"
  [style]="{ width: 'min(1180px, 96vw)' }"
  [breakpoints]="{ '900px': '97vw' }"
  styleClass="master-create-dialog labor-planning-dialog">
  <section class="master-dialog-body">
    <aside class="master-dialog-summary">
      <div class="summary-icon"><i class="pi pi-briefcase"></i></div>
      <div>
        <small>Field Work</small>
        <h3>{{ currentWork()?.workPackageNumber || 'Resource plan' }}</h3>
        <p>{{ currentWork()?.title }}</p>
      </div>
      <div class="master-dialog-facts">
        <div><span>Status</span><p-tag [value]="status" [severity]="severity(status)"></p-tag></div>
        <div><span>Labor coverage</span><strong>{{ laborCoverage().assigned }}/{{ laborCoverage().required }}</strong></div>
        <div><span>Equipment coverage</span><strong>{{ equipmentCoverage().allocated }}/{{ equipmentCoverage().required }}</strong></div>
      </div>
      <div class="next-action">
        <span>What to do next</span>
        <strong>{{ nextActionTitle }}</strong>
        <p>{{ nextActionText }}</p>
      </div>
    </aside>

    <div class="planning-body">
      <div class="workflow-steps">
        <div [class.done]="status !== 'DRAFT'" [class.active]="status === 'DRAFT'">
          <span>1</span><strong>Requirements</strong><small>People and equipment</small>
        </div>
        <div [class.done]="status === 'RELEASED' || status === 'IN_PROGRESS' || status === 'COMPLETED'"
          [class.active]="status === 'PLANNED'">
          <span>2</span><strong>Assignments</strong><small>Eligibility and availability</small>
        </div>
        <div [class.done]="status === 'IN_PROGRESS' || status === 'COMPLETED'" [class.active]="status === 'RELEASED'">
          <span>3</span><strong>Release</strong><small>Confirm the field plan</small>
        </div>
      </div>

      @if (error()) {
        <div class="dialog-error"><i class="pi pi-exclamation-circle"></i><span>{{ error() }}</span></div>
      }

      @if (currentWork()?.requirementOnly) {
        <div class="setup-hint requirement-stage-hint"><i class="pi pi-info-circle"></i>
          <span>This is a requirements-gathering target. Requirements propagate to the transport order release; people and equipment can be assigned only after that release enters truck or bulk planning, or on shipment delivery.</span>
        </div>
      }

      <section class="planning-section">
        <div class="section-heading">
          <div><span>Required labor</span><h4>Roles, headcount and capabilities</h4></div>
          <small>All options come from Operations / Field Work / Setup.</small>
        </div>

        @if (canManage && (status === 'DRAFT' || status === 'PLANNED')) {
          <div class="role-builder">
            <div class="role-form">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-briefcase"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <p-select inputId="fieldLaborRole" [options]="roleOptions()" optionLabel="label" optionValue="fieldRoleId"
                    [ngModel]="selectedRoleId()" (ngModelChange)="selectedRoleId.set($event)" [filter]="true"
                    filterBy="label,roleCode,roleName,workerCategory" [loading]="loadingSetup()" appendTo="body"
                    styleClass="w-full"></p-select>
                  <label for="fieldLaborRole">Role *</label>
                </p-iftalabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-users"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <p-inputNumber inputId="fieldLaborHeadcount" [ngModel]="headcount()"
                    (ngModelChange)="headcount.set($event || 1)" [min]="1" [max]="999"
                    [showButtons]="true" styleClass="w-full"></p-inputNumber>
                  <label for="fieldLaborHeadcount">Headcount *</label>
                </p-iftalabel>
              </p-inputGroup>
              <button pButton type="button" label="Add role" icon="pi pi-plus" [loading]="savingRequirement()"
                [disabled]="!selectedRoleId()" (click)="addRole()"></button>
            </div>

            <div class="activity-role-hint">
              <span><i class="pi pi-sparkles"></i>{{ activityRoleHint }}</span>
              @if (recommendedRoleOptions().length && allRoleOptions().length > recommendedRoleOptions().length) {
                <button pButton type="button" size="small" severity="secondary" text
                  [label]="showAllRoles() ? 'Show suggested roles' : 'Show all roles'"
                  (click)="toggleRoleScope()"></button>
              }
            </div>

            <div class="capability-builder">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-star"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <p-select inputId="fieldLaborCapability" [options]="capabilityOptions()" optionLabel="label"
                    optionValue="fieldCapabilityId" [filter]="true" filterBy="label,capabilityCode"
                    [ngModel]="selectedCapabilityId()" (ngModelChange)="selectedCapabilityId.set($event)"
                    [loading]="loadingSetup()" appendTo="body" styleClass="w-full"></p-select>
                  <label for="fieldLaborCapability">Capability (optional)</label>
                </p-iftalabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <p-select inputId="fieldCapabilityType"
                    [options]="[{label:'Mandatory', value:'MANDATORY'}, {label:'Preferred', value:'PREFERRED'}]"
                    optionLabel="label" optionValue="value" [ngModel]="capabilityRequirementType()"
                    (ngModelChange)="capabilityRequirementType.set($event)" styleClass="w-full"></p-select>
                  <label for="fieldCapabilityType">Requirement type</label>
                </p-iftalabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-chart-bar"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <p-select inputId="fieldMinimumProficiency" [options]="proficiencyLevels()" optionLabel="levelName"
                    optionValue="fieldProficiencyLevelId" [showClear]="true" [ngModel]="minimumProficiencyId()"
                    (ngModelChange)="minimumProficiencyId.set($event)" appendTo="body" styleClass="w-full"></p-select>
                  <label for="fieldMinimumProficiency">Minimum proficiency</label>
                </p-iftalabel>
              </p-inputGroup>
              <button pButton type="button" label="Attach capability" icon="pi pi-link" severity="secondary" outlined
                [disabled]="!selectedCapabilityId()" (click)="addCapabilityToDraft()"></button>
            </div>
            @if (draftSkills().length) {
              <div class="draft-chips">
                @for (skill of draftSkills(); track skill.fieldCapabilityId) {
                  <span [class.preferred]="skill.requirementType === 'PREFERRED'">
                    {{ skill.skillName }} · {{ skill.requirementType }}
                    @if (skill.minimumProficiencyLevel) { · {{ skill.minimumProficiencyLevel }} }
                    <button type="button" aria-label="Remove capability"
                      (click)="removeDraftCapability(skill.fieldCapabilityId)"><i class="pi pi-times"></i></button>
                  </span>
                }
              </div>
            }
          </div>
          @if (!loadingSetup() && !roleOptions().length && !error()) {
            <div class="setup-hint"><i class="pi pi-info-circle"></i>
              <span>No active roles are configured. Add roles under Operations / Field Work / Setup first.</span>
            </div>
          }
        }

        <div class="requirement-list">
          @for (requirement of currentWork()?.laborRequirements || []; track requirement.fieldLaborRequirementId) {
            <article class="requirement-card" [class.selected]="activeRequirementId() === requirement.fieldLaborRequirementId">
              <div class="requirement-main">
                <div class="role-icon"><i class="pi pi-user"></i></div>
                <div>
                  <strong>{{ requirement.roleName }}</strong>
                  <small>{{ requirement.roleCode || 'Field Work role' }}</small>
                  @if (requirement.skills?.length) {
                    <span class="skill-line">{{ requirementSkills(requirement) }}</span>
                  }
                </div>
              </div>
              <div class="coverage">
                <span>{{ assignedFor(requirement) }}/{{ requirement.headcount || 0 }}</span>
                <small>assigned</small>
              </div>
              <div class="requirement-actions">
                @if (currentWork()?.assignmentAllowed && canAssign && status === 'PLANNED') {
                  <button pButton type="button" label="Find candidates" icon="pi pi-search" size="small" outlined
                    (click)="showCandidates(requirement)"></button>
                } @else if (status === 'DRAFT') {
                  <span class="plan-first">Plan first</span>
                } @else if (currentWork()?.requirementOnly) {
                  <span class="plan-first">Assign downstream</span>
                }
                @if (canManage && (status === 'DRAFT' || status === 'PLANNED')) {
                  <button pButton type="button" label="Remove role" icon="pi pi-trash" size="small"
                    severity="danger" text [loading]="removingRequirementId() === requirement.fieldLaborRequirementId"
                    [disabled]="busy() && removingRequirementId() !== requirement.fieldLaborRequirementId"
                    (click)="removeRole(requirement)"></button>
                }
              </div>
              @if (assignmentsFor(requirement).length) {
                <div class="assigned-people">
                  @for (assignment of assignmentsFor(requirement); track assignment.fieldLaborAssignmentId) {
                    <span><i class="pi pi-check-circle"></i>{{ assignment.workerName }} — {{ assignment.sourceType }}</span>
                  }
                </div>
              }
            </article>
          } @empty {
            <div class="empty-state"><i class="pi pi-users"></i><strong>No labor roles yet</strong>
              <span>Select a Field Work role above and specify how many people this activity requires.</span></div>
          }
        </div>
      </section>

      @if (activeRequirement()) {
        <section class="planning-section candidates-section">
          <div class="section-heading">
            <div><span>People</span><h4>Candidates for {{ activeRequirement()?.roleName }}</h4></div>
            <small>Select up to {{ remainingLaborSlots() }} remaining person(s). Identity, payee and rate blockers are never overridable.</small>
          </div>
          @if (loadingCandidates()) {
            <div class="loading-state"><i class="pi pi-spin pi-spinner"></i> Checking capabilities, availability and rates...</div>
          } @else {
            <div class="candidate-list">
              @for (candidate of candidates(); track candidate.fieldWorkerId) {
                <article class="candidate-card" [class.blocked]="!candidate.eligible"
                  [class.selected]="isCandidateSelected(candidate)">
                  <div class="candidate-avatar"><i class="pi pi-user"></i></div>
                  <div class="candidate-copy">
                    <strong>{{ candidate.displayName }}</strong>
                    <span>{{ candidate.workerNumber }} — {{ candidate.sourceType }} — {{ candidate.workerCategory || 'General field worker' }}</span>
                    <small [class.warning]="candidate.warnings?.length" [class.danger]="candidate.blockers?.length">
                      {{ candidateMessage(candidate) }}
                    </small>
                    @if (canViewLaborRates && candidate.resolvedRate) {
                      <small class="resolved-price">Resolved: {{ candidate.resolvedRate.buyRate | number:'1.2-2' }} {{ candidate.resolvedRate.currencyCode }} buy
                        <ng-container *ngIf="candidate.resolvedRate.sellRate != null"> · {{ candidate.resolvedRate.sellRate | number:'1.2-2' }} sell</ng-container>
                        / {{ candidate.resolvedRate.billingUnit }}</small>
                    }
                    @if (canOverride && canViewLaborRates) {
                      <button pButton type="button" size="small" severity="secondary" outlined class="price-override-button"
                        [label]="laborPriceOverride(candidate)?.enabled ? 'Use resolved price' : 'Override assignment price'"
                        icon="pi pi-money-bill" (click)="toggleLaborPriceOverride(candidate)"></button>
                    }
                    @if ((!candidate.eligible && canAssignCandidate(candidate)) || laborPriceOverride(candidate)?.enabled) {
                      <p-inputGroup class="override-field">
                        <p-inputGroupAddon><i class="pi pi-file-edit"></i></p-inputGroupAddon>
                        <p-iftalabel>
                          <input pInputText type="text" [id]="'laborOverride-' + candidate.fieldWorkerId"
                            [(ngModel)]="overrideReasons[laborOverrideKey(candidate)]">
                          <label [attr.for]="'laborOverride-' + candidate.fieldWorkerId">Audited override reason *</label>
                        </p-iftalabel>
                      </p-inputGroup>
                    }
                  </div>
                  <p-tag [value]="candidate.eligible ? 'ELIGIBLE' : canAssignCandidate(candidate) ? 'OVERRIDE' : 'BLOCKED'"
                    [severity]="candidate.eligible ? 'success' : canAssignCandidate(candidate) ? 'warn' : 'danger'"></p-tag>
                  <button pButton type="button" [label]="isCandidateSelected(candidate) ? 'Selected' : 'Select'"
                    [icon]="isCandidateSelected(candidate) ? 'pi pi-check' : 'pi pi-plus'" size="small"
                    [severity]="isCandidateSelected(candidate) ? 'success' : 'secondary'"
                    [outlined]="!isCandidateSelected(candidate)"
                    [disabled]="!canSelectCandidate(candidate) || (!candidate.eligible && !overrideReasons[laborOverrideKey(candidate)])
                      || (candidateNeedsRateOverride(candidate) && !validLaborPriceOverride(candidate))"
                    (click)="toggleCandidate(candidate)"></button>
                  @if (laborPriceOverride(candidate)?.enabled) {
                    <div class="price-override-panel">
                      <div class="price-override-heading"><i class="pi pi-shield"></i><span>Assignment price override</span>
                        <small>These values are snapshotted on this person’s assignment only.</small></div>
                      <p-inputGroup><p-inputGroupAddon><i class="pi pi-arrow-down-left"></i></p-inputGroupAddon><p-iftalabel>
                        <p-inputNumber [inputId]="'laborOverrideBuy-' + candidate.fieldWorkerId"
                          [(ngModel)]="laborPriceOverrides[laborOverrideKey(candidate)].buyRate" [min]="0"
                          [minFractionDigits]="2" [maxFractionDigits]="4" styleClass="w-full"></p-inputNumber>
                        <label [attr.for]="'laborOverrideBuy-' + candidate.fieldWorkerId">Override buy rate</label></p-iftalabel></p-inputGroup>
                      <p-inputGroup><p-inputGroupAddon><i class="pi pi-arrow-up-right"></i></p-inputGroupAddon><p-iftalabel>
                        <p-inputNumber [inputId]="'laborOverrideSell-' + candidate.fieldWorkerId"
                          [(ngModel)]="laborPriceOverrides[laborOverrideKey(candidate)].sellRate" [min]="0"
                          [minFractionDigits]="2" [maxFractionDigits]="4" styleClass="w-full"></p-inputNumber>
                        <label [attr.for]="'laborOverrideSell-' + candidate.fieldWorkerId">Override sell rate</label></p-iftalabel></p-inputGroup>
                      <p-inputGroup><p-inputGroupAddon><i class="pi pi-clock"></i></p-inputGroupAddon><p-iftalabel>
                        <p-select [inputId]="'laborOverrideUnit-' + candidate.fieldWorkerId"
                          [(ngModel)]="laborPriceOverrides[laborOverrideKey(candidate)].billingUnit"
                          [options]="billingUnitOptions" appendTo="body" styleClass="w-full"></p-select>
                        <label [attr.for]="'laborOverrideUnit-' + candidate.fieldWorkerId">Billing unit *</label></p-iftalabel></p-inputGroup>
                      <p-inputGroup><p-inputGroupAddon><i class="pi pi-money-bill"></i></p-inputGroupAddon><p-iftalabel>
                        <input pInputText [id]="'laborOverrideCurrency-' + candidate.fieldWorkerId"
                          [(ngModel)]="laborPriceOverrides[laborOverrideKey(candidate)].currencyCode" maxlength="3">
                        <label [attr.for]="'laborOverrideCurrency-' + candidate.fieldWorkerId">Currency *</label></p-iftalabel></p-inputGroup>
                    </div>
                  }
                </article>
              } @empty {
                <div class="empty-state"><i class="pi pi-search"></i><strong>No candidates found</strong>
                  <span>Check the person, capabilities, availability and payee mapping under Operations / Field Work.</span></div>
              }
            </div>
            @if (candidates().length && remainingLaborSlots() > 0) {
              <div class="assignment-selection-bar">
                <span><strong>{{ selectedCandidateIds().length }}</strong> selected · {{ remainingLaborSlots() }} slot(s) remaining</span>
                <button pButton type="button"
                  [label]="selectedCandidateIds().length > 1 ? 'Assign selected laborers' : 'Assign selected laborer'"
                  icon="pi pi-users" [loading]="assigningSelection()" [disabled]="!assignmentSelectionReady()"
                  (click)="assignSelected()"></button>
              </div>
            } @else if (remainingLaborSlots() === 0) {
              <div class="assignment-selection-bar covered">
                <span><i class="pi pi-check-circle"></i> This labor requirement is fully covered.</span>
              </div>
            }
          }
        </section>
      }

      @if (canViewEquipment) {
        <section class="planning-section">
          <div class="section-heading">
            <div><span>Reusable resources</span><h4>Tools and equipment</h4></div>
            <small>Vehicles remain in Fleet; consumables and POSM materials remain in Warehousing.</small>
          </div>

          @if (canManageEquipment && (status === 'DRAFT' || status === 'PLANNED')) {
            <div class="equipment-form">
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-wrench"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <p-select inputId="fieldEquipmentCategory" [options]="categories()" optionLabel="categoryName"
                    optionValue="equipmentCategoryId" [filter]="true" filterBy="categoryName,categoryCode"
                    [ngModel]="selectedEquipmentCategoryId()" (ngModelChange)="selectedEquipmentCategoryId.set($event)"
                    [loading]="loadingSetup()" appendTo="body" styleClass="w-full"></p-select>
                  <label for="fieldEquipmentCategory">Equipment category *</label>
                </p-iftalabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-hashtag"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <p-inputNumber inputId="fieldEquipmentQuantity" [ngModel]="equipmentQuantity()"
                    (ngModelChange)="equipmentQuantity.set($event || 1)" [min]="1" [max]="999"
                    [showButtons]="true" styleClass="w-full"></p-inputNumber>
                  <label for="fieldEquipmentQuantity">Quantity *</label>
                </p-iftalabel>
              </p-inputGroup>
              <p-inputGroup>
                <p-inputGroupAddon><i class="pi pi-shield"></i></p-inputGroupAddon>
                <p-iftalabel>
                  <p-select inputId="fieldEquipmentRequirementType"
                    [options]="[{label:'Mandatory', value:'MANDATORY'}, {label:'Preferred', value:'PREFERRED'}]"
                    optionLabel="label" optionValue="value" [ngModel]="equipmentRequirementType()"
                    (ngModelChange)="equipmentRequirementType.set($event)" styleClass="w-full"></p-select>
                  <label for="fieldEquipmentRequirementType">Requirement type</label>
                </p-iftalabel>
              </p-inputGroup>
              <button pButton type="button" label="Add equipment" icon="pi pi-plus"
                [loading]="savingEquipmentRequirement()" [disabled]="!selectedEquipmentCategoryId()"
                (click)="addEquipmentRequirement()"></button>
            </div>
            @if (!loadingSetup() && !categories().length) {
              <div class="setup-hint"><i class="pi pi-info-circle"></i>
                <span>No equipment categories are configured. Add them under Operations / Field Work / Tools & Equipment.</span>
              </div>
            }
          }

          <div class="requirement-list">
            @for (requirement of currentWork()?.equipmentRequirements || []; track requirement.fieldEquipmentRequirementId) {
              <article class="requirement-card" [class.selected]="activeEquipmentRequirementId() === requirement.fieldEquipmentRequirementId">
                <div class="requirement-main">
                  <div class="role-icon equipment-icon"><i class="pi pi-wrench"></i></div>
                  <div>
                    <strong>{{ requirement.categoryName }}</strong>
                    <small>{{ requirement.requirementType || 'MANDATORY' }} · {{ requirement.locationName || currentWork()?.locationName || 'Package location' }}</small>
                  </div>
                </div>
                <div class="coverage">
                  <span>{{ allocatedFor(requirement) }}/{{ requirement.requiredQuantity || 0 }}</span>
                  <small>reserved</small>
                </div>
                @if (currentWork()?.assignmentAllowed && canAssign && status === 'PLANNED') {
                  <button pButton type="button" label="Find equipment" icon="pi pi-search" size="small" outlined
                    (click)="showEquipmentCandidates(requirement)"></button>
                } @else if (status === 'DRAFT') {
                  <span class="plan-first">Plan first</span>
                } @else if (currentWork()?.requirementOnly) {
                  <span class="plan-first">Reserve downstream</span>
                }
                @if (allocationsFor(requirement).length) {
                  <div class="assigned-people equipment-allocations">
                    @for (allocation of allocationsFor(requirement); track allocation.fieldEquipmentAllocationId) {
                      <span><i class="pi pi-check-circle"></i>{{ allocation.equipmentResourceName }} — {{ allocation.plannedQuantity }} {{ allocation.allocationStatus }}</span>
                    }
                  </div>
                }
              </article>
            } @empty {
              <div class="empty-state"><i class="pi pi-wrench"></i><strong>No equipment requirements</strong>
                <span>Add reusable resources only when this activity needs them.</span></div>
            }
          </div>
        </section>
      }

      @if (activeEquipmentRequirement()) {
        <section class="planning-section candidates-section">
          <div class="section-heading">
            <div><span>Equipment availability</span><h4>{{ activeEquipmentRequirement()?.categoryName }}</h4></div>
            <small>Maintenance, lost status and expired inspection are never overridable.</small>
          </div>
          @if (loadingEquipmentCandidates()) {
            <div class="loading-state"><i class="pi pi-spin pi-spinner"></i> Checking reservations, condition and location...</div>
          } @else {
            <div class="candidate-list">
              @for (candidate of equipmentCandidates(); track candidate.equipmentResourceId) {
                <article class="candidate-card" [class.blocked]="!candidate.available">
                  <div class="candidate-avatar equipment-icon"><i class="pi pi-wrench"></i></div>
                  <div class="candidate-copy">
                    <strong>{{ candidate.resourceName }}</strong>
                    <span>{{ candidate.resourceCode }} — {{ candidate.ownershipType }} — {{ candidate.availableQuantity || 0 }} available</span>
                    <small [class.warning]="candidate.warnings?.length" [class.danger]="candidate.blockers?.length">
                      {{ equipmentCandidateMessage(candidate) }}
                    </small>
                    @if (canOverride && canViewEquipmentRates) {
                      <button pButton type="button" size="small" severity="secondary" outlined class="price-override-button"
                        [label]="equipmentPriceOverride(candidate)?.enabled ? 'Use resolved price' : 'Override reservation price'"
                        icon="pi pi-money-bill" (click)="toggleEquipmentPriceOverride(candidate)"></button>
                    }
                    @if ((!candidate.available && canReserveCandidate(candidate)) || equipmentPriceOverride(candidate)?.enabled) {
                      <p-inputGroup class="override-field">
                        <p-inputGroupAddon><i class="pi pi-file-edit"></i></p-inputGroupAddon>
                        <p-iftalabel>
                          <input pInputText type="text" [id]="'equipmentOverride-' + candidate.equipmentResourceId"
                            [(ngModel)]="overrideReasons[equipmentOverrideKey(candidate)]">
                          <label [attr.for]="'equipmentOverride-' + candidate.equipmentResourceId">Audited override reason *</label>
                        </p-iftalabel>
                      </p-inputGroup>
                    }
                  </div>
                  <p-tag [value]="candidate.available ? 'AVAILABLE' : canReserveCandidate(candidate) ? 'OVERRIDE' : 'BLOCKED'"
                    [severity]="candidate.available ? 'success' : canReserveCandidate(candidate) ? 'warn' : 'danger'"></p-tag>
                  <button pButton type="button" [label]="candidate.available ? 'Reserve' : 'Override & reserve'"
                    icon="pi pi-bookmark" size="small"
                    [disabled]="!canReserveCandidate(candidate) || (!candidate.available && !overrideReasons[equipmentOverrideKey(candidate)])
                      || (equipmentPriceOverride(candidate)?.enabled && !validEquipmentPriceOverride(candidate))"
                    [loading]="reservingResourceId() === candidate.equipmentResourceId" (click)="reserveEquipment(candidate)"></button>
                  @if (equipmentPriceOverride(candidate)?.enabled) {
                    <div class="price-override-panel">
                      <div class="price-override-heading"><i class="pi pi-shield"></i><span>Reservation price override</span>
                        <small>These values are snapshotted on this equipment allocation only.</small></div>
                      <p-inputGroup><p-inputGroupAddon><i class="pi pi-arrow-down-left"></i></p-inputGroupAddon><p-iftalabel>
                        <p-inputNumber [inputId]="'equipmentOverrideBuy-' + candidate.equipmentResourceId"
                          [(ngModel)]="equipmentPriceOverrides[equipmentOverrideKey(candidate)].buyRate" [min]="0"
                          [minFractionDigits]="2" [maxFractionDigits]="4" styleClass="w-full"></p-inputNumber>
                        <label [attr.for]="'equipmentOverrideBuy-' + candidate.equipmentResourceId">Override buy rate</label></p-iftalabel></p-inputGroup>
                      <p-inputGroup><p-inputGroupAddon><i class="pi pi-arrow-up-right"></i></p-inputGroupAddon><p-iftalabel>
                        <p-inputNumber [inputId]="'equipmentOverrideSell-' + candidate.equipmentResourceId"
                          [(ngModel)]="equipmentPriceOverrides[equipmentOverrideKey(candidate)].sellRate" [min]="0"
                          [minFractionDigits]="2" [maxFractionDigits]="4" styleClass="w-full"></p-inputNumber>
                        <label [attr.for]="'equipmentOverrideSell-' + candidate.equipmentResourceId">Override sell rate</label></p-iftalabel></p-inputGroup>
                      <p-inputGroup><p-inputGroupAddon><i class="pi pi-clock"></i></p-inputGroupAddon><p-iftalabel>
                        <p-select [inputId]="'equipmentOverrideUnit-' + candidate.equipmentResourceId"
                          [(ngModel)]="equipmentPriceOverrides[equipmentOverrideKey(candidate)].billingUnit"
                          [options]="billingUnitOptions" appendTo="body" styleClass="w-full"></p-select>
                        <label [attr.for]="'equipmentOverrideUnit-' + candidate.equipmentResourceId">Billing unit *</label></p-iftalabel></p-inputGroup>
                      <p-inputGroup><p-inputGroupAddon><i class="pi pi-money-bill"></i></p-inputGroupAddon><p-iftalabel>
                        <input pInputText [id]="'equipmentOverrideCurrency-' + candidate.equipmentResourceId"
                          [(ngModel)]="equipmentPriceOverrides[equipmentOverrideKey(candidate)].currencyCode" maxlength="3">
                        <label [attr.for]="'equipmentOverrideCurrency-' + candidate.equipmentResourceId">Currency *</label></p-iftalabel></p-inputGroup>
                    </div>
                  }
                </article>
              } @empty {
                <div class="empty-state"><i class="pi pi-search"></i><strong>No matching equipment found</strong>
                  <span>Add active resources to this category or adjust the planned interval.</span></div>
              }
            </div>
          }
        </section>
      }
    </div>
  </section>

  <ng-template pTemplate="footer">
    <div class="master-dialog-footer">
      <button pButton type="button" label="Close" icon="pi pi-times" severity="secondary" outlined
        [disabled]="busy()" (click)="close()"></button>
      <span class="footer-spacer"></span>
      @if (canRelease && status === 'DRAFT') {
        <button pButton type="button" label="Plan package" icon="pi pi-calendar-plus"
          [loading]="transitioning()" [disabled]="!hasRequirements()" (click)="transition('PLANNED')"></button>
      }
      @if (canRelease && currentWork()?.assignmentAllowed && status === 'PLANNED') {
        <button pButton type="button" label="Release package" icon="pi pi-send"
          [loading]="transitioning()" [disabled]="!laborCovered() || !equipmentCovered()"
          (click)="transition('RELEASED')"></button>
      }
    </div>
  </ng-template>
</p-dialog>
